import { useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { formatDate } from "../misc/formatDate";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Sponsoring } from "../types";
import { getEndDateStatus } from "./sponsoringUtils";

/**
 * "Verlopen" badge when the end date is in the past, "Loopt over N dagen af"
 * when it is within the next 90 days, nothing otherwise.
 */
export const EndDateStatus = ({ endDate }: { endDate?: string | null }) => {
  const translate = useTranslate();
  const status = getEndDateStatus(endDate);
  if (!status) return null;

  if (status.kind === "expired") {
    return (
      <Badge variant="destructive" className="whitespace-nowrap">
        {translate("resources.sponsorings.expired")}
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className={cn(
        "whitespace-nowrap border-transparent",
        status.days <= 30
          ? "bg-amber-100 text-amber-900"
          : "bg-muted text-muted-foreground",
      )}
    >
      {status.kind === "today"
        ? translate("resources.sponsorings.ends_today")
        : translate("resources.sponsorings.ends_in", {
            smart_count: status.days,
          })}
    </Badge>
  );
};

/**
 * Formatted end date followed by its status badge. The badge only makes
 * sense for running sponsorings: leads and closed ones never show it.
 * Renders "Geen einddatum" when there is no end date.
 */
export const EndDateField = ({
  record,
  className,
}: {
  record: Pick<Sponsoring, "end_date" | "stage">;
  className?: string;
}) => {
  const translate = useTranslate();
  const { sponsoringActiveStatuses } = useConfigurationContext();

  if (!record.end_date) {
    return (
      <span className="text-muted-foreground">
        {translate("resources.sponsorings.no_end_date")}
      </span>
    );
  }

  const isActive = sponsoringActiveStatuses.includes(record.stage);
  const formatted =
    formatDate(record.end_date) ||
    translate("resources.sponsorings.invalid_date");

  return (
    <span className={cn("inline-flex flex-wrap items-center gap-2", className)}>
      <span className="whitespace-nowrap">{formatted}</span>
      {isActive ? <EndDateStatus endDate={record.end_date} /> : null}
    </span>
  );
};
